/**
 * AI Editor view — three tabs: Knowledge, Personality, Facts
 * Admin-only. Manages AI Support's configuration (and eventually other AI users).
 */

import { render, showToast, showApiError } from '../main';
import { aiEditorApi } from '../api';
import { store } from '../state';

const AI_USER_ID = 'AISUP1';

const FACT_CATEGORIES = ['backstory', 'demographic', 'opinion', 'preference', 'quirk', 'memory'] as const;

const EMOJI_FREQUENCIES = ['never', 'rare', 'moderate', 'frequent'] as const;
const MESSAGE_LENGTHS = ['terse', 'average', 'verbose'] as const;
const RESPONSE_STYLES = ['direct', 'storyteller', 'questioner', 'supportive', 'playful'] as const;
const HUMOR_STYLES = ['dry', 'punny', 'self_deprecating', 'sarcastic', 'absurdist', 'none'] as const;
const SWEARING_LEVELS = ['never', 'rare', 'moderate', 'frequent'] as const;

// ============================================================================
// Main render
// ============================================================================

export async function renderKnowledge(): Promise<void> {
  const isAdmin = !!store.getState().user?.email?.endsWith('@914-6.com');
  if (!isAdmin) { window.location.hash = '#/browse'; return; }

  render(`
    <div class="view-frame profile-edit-container">
      <div class="seg-bar"><div class="seg seg-full">
        <button class="seg-btn active" data-tab="knowledge">Knowledge</button>
        <button class="seg-btn" data-tab="personality">Personality</button>
        <button class="seg-btn" data-tab="facts">Facts</button>
      </div></div>

      <div class="view-scroll seg-panel active" id="tab-knowledge">
        <div class="profile-edit-form" id="knowledge-container">
          <p class="text-muted">Loading...</p>
        </div>
      </div>

      <div class="view-scroll seg-panel" id="tab-personality">
        <div class="profile-edit-form" id="personality-container">
          <p class="text-muted">Loading...</p>
        </div>
      </div>

      <div class="view-scroll seg-panel" id="tab-facts">
        <div class="profile-edit-form" id="facts-container">
          <p class="text-muted">Loading...</p>
        </div>
      </div>
    </div>
  `);

  setupTabs();
  loadKnowledge();
  loadPersonality();
  loadFacts();
}

// ============================================================================
// Tab switching
// ============================================================================

function setupTabs(): void {
  const tabs = document.querySelectorAll('.seg-btn');
  const contents = document.querySelectorAll('.seg-panel');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const tabName = (tab as HTMLElement).dataset.tab;
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      contents.forEach(c => c.classList.remove('active'));
      document.getElementById(`tab-${tabName}`)?.classList.add('active');
    });
  });
}

// ============================================================================
// Knowledge tab
// ============================================================================

async function loadKnowledge(): Promise<void> {
  const container = document.getElementById('knowledge-container')!;
  try {
    const res = await aiEditorApi.listSections(AI_USER_ID);
    const sections: any[] = res.data;
    renderKnowledgeSections(container, sections);
  } catch (err) {
    container.innerHTML = '<p class="text-muted">Failed to load knowledge sections.</p>';
  }
}

function renderKnowledgeSections(container: HTMLElement, sections: any[]): void {
  container.innerHTML = `
    <button class="btn btn-secondary" id="add-section-btn" style="align-self: flex-start;">
      <i class="fas fa-plus"></i> Add Section
    </button>
    ${sections.map((s, i) => `
      <div class="card knowledge-section" data-id="${s.id}" data-index="${i}">
        <div class="knowledge-row">
          <div class="input-group" style="flex: 1;">
            <label class="group-label">Key</label>
            <input type="text" class="input ks-key" value="${esc(s.section_key)}" />
          </div>
          <div class="input-group" style="flex: 2;">
            <label class="group-label">Title</label>
            <input type="text" class="input ks-title" value="${esc(s.section_title)}" />
          </div>
        </div>
        <div class="input-group">
          <label class="group-label">Content</label>
          <textarea class="input knowledge-textarea ks-content" rows="7">${esc(s.content)}</textarea>
        </div>
        <div class="knowledge-actions">
          <button class="btn btn-text ks-up" ${i === 0 ? 'disabled' : ''}><i class="fas fa-arrow-up"></i></button>
          <button class="btn btn-text ks-down" ${i === sections.length - 1 ? 'disabled' : ''}><i class="fas fa-arrow-down"></i></button>
          <button class="btn btn-primary ks-save"><i class="fas fa-save"></i> Save</button>
          <button class="btn btn-danger-outline ks-delete"><i class="fas fa-trash"></i></button>
        </div>
      </div>
    `).join('')}
  `;

  // Add section
  container.querySelector('#add-section-btn')!.addEventListener('click', async () => {
    try {
      const newSection = await aiEditorApi.createSection(AI_USER_ID, {
        section_key: 'new_section',
        section_title: 'New Section',
        content: '',
        sort_order: sections.length,
      });
      sections.push(newSection.data);
      renderKnowledgeSections(container, sections);
      showToast('Section created', 'success');
    } catch (err) { showApiError(err, 'Failed to create section'); }
  });

  // Per-section handlers
  container.querySelectorAll('.knowledge-section').forEach(card => {
    const el = card as HTMLElement;
    const id = el.dataset.id!;
    const idx = parseInt(el.dataset.index!, 10);

    el.querySelector('.ks-save')!.addEventListener('click', async () => {
      try {
        await aiEditorApi.updateSection(AI_USER_ID, id, {
          section_key: (el.querySelector('.ks-key') as HTMLInputElement).value,
          section_title: (el.querySelector('.ks-title') as HTMLInputElement).value,
          content: (el.querySelector('.ks-content') as HTMLTextAreaElement).value,
        });
        showToast('Section saved', 'success');
      } catch (err) { showApiError(err, 'Failed to save section'); }
    });

    el.querySelector('.ks-delete')!.addEventListener('click', async () => {
      if (!confirm('Delete this section?')) return;
      try {
        await aiEditorApi.deleteSection(AI_USER_ID, id);
        sections.splice(idx, 1);
        renderKnowledgeSections(container, sections);
        showToast('Section deleted', 'success');
      } catch (err) { showApiError(err, 'Failed to delete section'); }
    });

    el.querySelector('.ks-up')!.addEventListener('click', async () => {
      if (idx === 0) return;
      [sections[idx - 1], sections[idx]] = [sections[idx], sections[idx - 1]];
      const ids = sections.map(s => s.id);
      try {
        await aiEditorApi.reorderSections(AI_USER_ID, ids);
        renderKnowledgeSections(container, sections);
      } catch (err) { showApiError(err, 'Failed to reorder'); }
    });

    el.querySelector('.ks-down')!.addEventListener('click', async () => {
      if (idx === sections.length - 1) return;
      [sections[idx], sections[idx + 1]] = [sections[idx + 1], sections[idx]];
      const ids = sections.map(s => s.id);
      try {
        await aiEditorApi.reorderSections(AI_USER_ID, ids);
        renderKnowledgeSections(container, sections);
      } catch (err) { showApiError(err, 'Failed to reorder'); }
    });
  });
}

// ============================================================================
// Personality tab
// ============================================================================

async function loadPersonality(): Promise<void> {
  const container = document.getElementById('personality-container')!;
  try {
    const res = await aiEditorApi.getPersonality(AI_USER_ID);
    const p = res.data || {};
    renderPersonalityForm(container, p);
  } catch (err) {
    container.innerHTML = '<p class="text-muted">Failed to load personality.</p>';
  }
}

function renderPersonalityForm(container: HTMLElement, p: any): void {
  const axes = [
    { key: 'axis_introvert_extrovert', low: 'Introvert', high: 'Extrovert' },
    { key: 'axis_serious_playful', low: 'Serious', high: 'Playful' },
    { key: 'axis_reserved_expressive', low: 'Reserved', high: 'Expressive' },
    { key: 'axis_analytical_emotional', low: 'Analytical', high: 'Emotional' },
    { key: 'axis_traditional_progressive', low: 'Traditional', high: 'Progressive' },
    { key: 'axis_cynical_optimistic', low: 'Cynical', high: 'Optimistic' },
  ];

  container.innerHTML = `
    <h3 class="mb-3">Personality Axes</h3>
    ${axes.map(a => `
      <div class="personality-axis">
        <span class="personality-axis-label">${a.low}</span>
        <input type="range" min="0" max="100" value="${p[a.key] ?? 50}" class="personality-axis-range" data-key="${a.key}" />
        <span class="personality-axis-label">${a.high}</span>
        <span class="personality-axis-value">${p[a.key] ?? 50}</span>
      </div>
    `).join('')}

    <h3 class="mb-3" style="margin-top: 1.5rem;">Communication</h3>
    <div class="input-group">
      <label class="group-label">Emoji Frequency</label>
      <select class="input" id="p-emoji">${EMOJI_FREQUENCIES.map(v => `<option ${p.comm_emoji_frequency === v ? 'selected' : ''}>${v}</option>`).join('')}</select>
    </div>
    <div class="input-group">
      <label class="group-label">Message Length</label>
      <select class="input" id="p-length">${MESSAGE_LENGTHS.map(v => `<option ${p.comm_message_length === v ? 'selected' : ''}>${v}</option>`).join('')}</select>
    </div>
    <div class="input-group">
      <label class="group-label">Response Style</label>
      <select class="input" id="p-style">${RESPONSE_STYLES.map(v => `<option ${p.comm_response_style === v ? 'selected' : ''}>${v}</option>`).join('')}</select>
    </div>
    <div class="input-group">
      <label class="group-label">Humor Style</label>
      <select class="input" id="p-humor">${HUMOR_STYLES.map(v => `<option ${p.comm_humor_style === v ? 'selected' : ''}>${v}</option>`).join('')}</select>
    </div>
    <div class="input-group">
      <label class="group-label">Swearing Level</label>
      <select class="input" id="p-swear">${SWEARING_LEVELS.map(v => `<option ${p.comm_swearing_level === v ? 'selected' : ''}>${v}</option>`).join('')}</select>
    </div>
    <div class="input-group">
      <label class="group-label">Texting Quirks <small class="text-muted">(comma-separated)</small></label>
      <input type="text" class="input" id="p-quirks" value="${esc(parseQuirks(p.comm_texting_quirks))}" />
    </div>

    <h3 class="mb-3" style="margin-top: 1.5rem;">Backstory & Summary</h3>
    <div class="input-group">
      <label class="group-label">Backstory</label>
      <textarea class="input knowledge-textarea" id="p-backstory" rows="7">${esc(p.backstory_full || '')}</textarea>
    </div>
    <div class="input-group">
      <label class="group-label">Personality Summary</label>
      <textarea class="input" id="p-summary" rows="3">${esc(p.personality_summary || '')}</textarea>
    </div>

    <button class="btn btn-primary" id="save-personality" style="align-self: flex-start; margin-top: 0.5rem;">
      <i class="fas fa-save"></i> Save All
    </button>
  `;

  // Live value display for range sliders
  container.querySelectorAll('.personality-axis-range').forEach(range => {
    range.addEventListener('input', () => {
      const el = range as HTMLInputElement;
      el.parentElement!.querySelector('.personality-axis-value')!.textContent = el.value;
    });
  });

  // Save
  container.querySelector('#save-personality')!.addEventListener('click', async () => {
    const data: Record<string, unknown> = {};
    container.querySelectorAll('.personality-axis-range').forEach(r => {
      const el = r as HTMLInputElement;
      data[el.dataset.key!] = parseInt(el.value, 10);
    });
    data.comm_emoji_frequency = (document.getElementById('p-emoji') as HTMLSelectElement).value;
    data.comm_message_length = (document.getElementById('p-length') as HTMLSelectElement).value;
    data.comm_response_style = (document.getElementById('p-style') as HTMLSelectElement).value;
    data.comm_humor_style = (document.getElementById('p-humor') as HTMLSelectElement).value;
    data.comm_swearing_level = (document.getElementById('p-swear') as HTMLSelectElement).value;
    const quirksStr = (document.getElementById('p-quirks') as HTMLInputElement).value;
    data.comm_texting_quirks = quirksStr.split(',').map(s => s.trim()).filter(Boolean);
    data.backstory_full = (document.getElementById('p-backstory') as HTMLTextAreaElement).value;
    data.personality_summary = (document.getElementById('p-summary') as HTMLTextAreaElement).value;

    try {
      await aiEditorApi.savePersonality(AI_USER_ID, data);
      showToast('Personality saved', 'success');
    } catch (err) { showApiError(err, 'Failed to save personality'); }
  });
}

function parseQuirks(val: any): string {
  if (!val) return '';
  if (typeof val === 'string') {
    try { return JSON.parse(val).join(', '); } catch { return val; }
  }
  if (Array.isArray(val)) return val.join(', ');
  return '';
}

// ============================================================================
// Facts tab
// ============================================================================

async function loadFacts(): Promise<void> {
  const container = document.getElementById('facts-container')!;
  try {
    const res = await aiEditorApi.listFacts(AI_USER_ID);
    const facts: any[] = res.data;
    renderFactsList(container, facts);
  } catch (err) {
    container.innerHTML = '<p class="text-muted">Failed to load facts.</p>';
  }
}

function renderFactsList(container: HTMLElement, facts: any[]): void {
  // Group by category
  const grouped = new Map<string, any[]>();
  for (const f of facts) {
    const cat = f.fact_category || 'other';
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(f);
  }

  container.innerHTML = `
    <button class="btn btn-secondary" id="add-fact-btn" style="align-self: flex-start;">
      <i class="fas fa-plus"></i> Add Fact
    </button>
    ${Array.from(grouped.entries()).map(([cat, catFacts]) => `
      <h3 style="margin-top: 1rem; text-transform: capitalize;">${cat}</h3>
      ${catFacts.map(f => renderFactRow(f)).join('')}
    `).join('')}
  `;

  // Add fact
  container.querySelector('#add-fact-btn')!.addEventListener('click', async () => {
    try {
      const newFact = await aiEditorApi.createFact(AI_USER_ID, {
        fact_category: 'backstory', fact_key: 'new_fact', fact_value: '',
      });
      facts.push(newFact.data);
      renderFactsList(container, facts);
      showToast('Fact created', 'success');
    } catch (err) { showApiError(err, 'Failed to create fact'); }
  });

  // Per-fact handlers
  container.querySelectorAll('.fact-row').forEach(row => {
    const el = row as HTMLElement;
    const id = el.dataset.id!;

    el.querySelector('.fact-save')!.addEventListener('click', async () => {
      try {
        await aiEditorApi.updateFact(AI_USER_ID, id, {
          fact_category: (el.querySelector('.fact-cat') as HTMLSelectElement).value,
          fact_key: (el.querySelector('.fact-key') as HTMLInputElement).value,
          fact_value: (el.querySelector('.fact-val') as HTMLInputElement).value,
        });
        showToast('Fact saved', 'success');
      } catch (err) { showApiError(err, 'Failed to save fact'); }
    });

    el.querySelector('.fact-delete')!.addEventListener('click', async () => {
      if (!confirm('Delete this fact?')) return;
      try {
        await aiEditorApi.deleteFact(AI_USER_ID, id);
        const idx = facts.findIndex(f => f.id === id);
        if (idx >= 0) facts.splice(idx, 1);
        renderFactsList(container, facts);
        showToast('Fact deleted', 'success');
      } catch (err) { showApiError(err, 'Failed to delete fact'); }
    });
  });
}

function renderFactRow(f: any): string {
  return `
    <div class="fact-row" data-id="${f.id}">
      <select class="input fact-cat">
        ${FACT_CATEGORIES.map(c => `<option ${f.fact_category === c ? 'selected' : ''}>${c}</option>`).join('')}
      </select>
      <input type="text" class="input fact-key" value="${esc(f.fact_key)}" placeholder="key" />
      <input type="text" class="input fact-val" value="${esc(f.fact_value)}" placeholder="value" />
      <button class="btn btn-primary fact-save"><i class="fas fa-save"></i></button>
      <button class="btn btn-danger-outline fact-delete"><i class="fas fa-trash"></i></button>
    </div>
  `;
}

// ============================================================================
// Helpers
// ============================================================================

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
